import { useEffect } from 'react'
import type { CSSProperties } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useNavigate } from 'react-router-dom'
import { useLandingPersona } from '@/hooks/useLandingPersona'
import { useSwipePersona } from '@/hooks/useSwipePersona'
import { HAS_AUTH, loginRequest } from '@/lib/auth'
import LandingNav from '@/components/landing/LandingNav'
import LandingHero from '@/components/landing/LandingHero'
import LandingMarquee from '@/components/landing/LandingMarquee'
import LandingTwoKindsOfWork from '@/components/landing/LandingTwoKindsOfWork'
import LandingFeatures from '@/components/landing/LandingFeatures'
import LandingDayInLife from '@/components/landing/LandingDayInLife'
import LandingHowItWorks from '@/components/landing/LandingHowItWorks'
import LandingPersonaShowcase from '@/components/landing/LandingPersonaShowcase'
import LandingDemo from '@/components/landing/LandingDemo'
import LandingPricing from '@/components/landing/LandingPricing'
import LandingFAQ from '@/components/landing/LandingFAQ'
import LandingCTA from '@/components/landing/LandingCTA'
import LandingFooter from '@/components/landing/LandingFooter'
import Seo from '@/components/Seo'
import '@/styles/landing.css'

export default function Landing() {
  const { persona, personaId, setPersonaId, PERSONAS } = useLandingPersona()
  const swipe = useSwipePersona(personaId, setPersonaId)
  const { instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()

  // Bounce authenticated users into the app the moment MSAL flips. We used to
  // wait here for /users/me before deciding billing vs. setup vs. dashboard,
  // but that race left users stranded on the marketing page when /users/me
  // returned 404 before /users/provision finished. The downstream gates
  // (SubscriptionGuard for billing, Setup view for onboarding) handle the
  // smarter routing once user data lands.
  useEffect(() => {
    if (!HAS_AUTH) return
    if (!isAuthenticated) return
    navigate('/dashboard', { replace: true })
  }, [isAuthenticated, navigate])

  const signIn = () => {
    if (!HAS_AUTH) {
      window.location.href = '/dashboard'
      return
    }
    instance.loginRedirect(loginRequest).catch((e) => {
      // Surface failures so they don't silently no-op. Common causes:
      // "interaction_in_progress" (a prior redirect started), or a redirectUri
      // mismatch in the Entra app registration.
      // eslint-disable-next-line no-console
      console.error('[auth] loginRedirect failed', e)
    })
  }

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const goPricing = () => scrollTo('pricing')
  const goDemo = () => scrollTo('demo')

  const rootStyle: CSSProperties & Record<string, string> = {
    '--lp-primary': persona.primary,
    '--lp-secondary': persona.secondary,
    '--lp-glow': persona.glow,
    '--lp-bg-tint': persona.bgTint,
    '--lp-radius': persona.sharp ? '0px' : '14px',
    '--lp-radius-sm': persona.sharp ? '0px' : '10px',
    '--lp-radius-lg': persona.sharp ? '0px' : '24px',
  }

  return (
    <div className="landing-root" style={rootStyle} data-sharp={persona.sharp}>
      <Seo
        title="Jarvis — AI Chief of Staff for Solo Business Owners"
        description="A private AI chief of staff you call by phone. It reads your Facebook Ads and Stripe data out loud and briefs you every morning — two days a week back."
        path="/"
      />
      <div className="landing-bg" />

      <LandingNav
        persona={persona}
        personaId={personaId}
        setPersonaId={setPersonaId}
        PERSONAS={PERSONAS}
        onSignIn={signIn}
        onCta={goPricing}
      />

      <LandingHero persona={persona} onPrimary={goPricing} onSecondary={goDemo} swipe={swipe} />

      <LandingMarquee />

      <LandingTwoKindsOfWork persona={persona} />

      <LandingFeatures persona={persona} />

      <LandingDayInLife persona={persona} />

      <LandingHowItWorks persona={persona} />

      <LandingPersonaShowcase
        persona={persona}
        personaId={personaId}
        setPersonaId={setPersonaId}
        PERSONAS={PERSONAS}
      />

      <LandingDemo persona={persona} />

      <LandingPricing persona={persona} onSignIn={signIn} />

      <LandingFAQ persona={persona} />

      <LandingCTA persona={persona} onCta={goPricing} onSignIn={signIn} />

      <LandingFooter persona={persona} />
    </div>
  )
}
